import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, MessageSquare, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import OTPInput from '../components/OTPInput';
import whatsappAuthService from '../services/whatsappAuthService';
import { useAuthStore } from '../store/authStore';

type Step = 'phone' | 'otp';

const WhatsAppAuth: React.FC = () => {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();

  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [name, setName] = useState('');
  const [otp, setOtp] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [otpExpiry, setOtpExpiry] = useState<number | null>(null);
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (otpExpiry) {
      const interval = setInterval(() => {
        const remaining = Math.max(0, Math.floor((otpExpiry - Date.now()) / 1000));
        setTimeLeft(remaining);
        if (remaining === 0) {
          clearInterval(interval);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [otpExpiry]);

  const formatPhoneNumber = (value: string) => {
    return value.replace(/\D/g, '').slice(0, 10);
  };

  const getFullPhoneNumber = () => {
    return countryCode.replace('+', '') + phone;
  };

  const handleRequestOTP = async () => {
    setError('');
    setSuccess('');

    if (!phone || phone.length < 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);

    try {
      const fullPhone = getFullPhoneNumber();

      // Check if user already exists
      const checkRes = await whatsappAuthService.checkUser(fullPhone);
      const userExists = checkRes.data.exists;
      setIsNewUser(!userExists);

      if (!userExists && !name.trim()) {
        setError('Please enter your name to register');
        setLoading(false);
        return;
      }

      // Request OTP from backend
      const otpRes = await whatsappAuthService.requestOTP(fullPhone, name || undefined);
      const expiresIn = otpRes.data.expiresIn || 600;
      const validityMinutes = Math.floor(expiresIn / 60);

      setSuccess(`OTP sent successfully to your WhatsApp. Valid for ${validityMinutes} minutes.`);
      setOtpExpiry(Date.now() + expiresIn * 1000);
      setOtp('');
      setStep('otp');
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to send OTP. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    setError('');
    setSuccess('');

    if (otp.length !== 6) {
      setError('Please enter the complete 6-digit OTP');
      return;
    }

    setLoading(true);

    try {
      const fullPhone = getFullPhoneNumber();
      let authRes;

      if (isNewUser) {
        authRes = await whatsappAuthService.verifyAndRegister(fullPhone, otp, name.trim());
      } else {
        authRes = await whatsappAuthService.login(fullPhone, otp);
      }

      const { accessToken, refreshToken, user } = authRes.data;

      // Store tokens in local storage
      whatsappAuthService.storeTokens(accessToken, refreshToken);

      // Update auth state
      setAuth(
        {
          id: user.id,
          name: user.name,
          phone: user.phone,
          email: '',
          role: user.role,
          createdAt: new Date().toISOString(),
        },
        accessToken,
        refreshToken,
      );

      setSuccess(isNewUser ? 'Registration successful!' : 'Login successful!');
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Verification failed. Please try again.';
      setError(msg);
      setOtp('');
    } finally {
      setLoading(false);
    }
  };

  const handleAlreadyHaveOTP = () => {
    setError('');
    setSuccess('');
    setOtp('');
    setOtpExpiry(null);
    setStep('otp');
  };

  const handleResendOTP = async () => {
    setOtp('');
    await handleRequestOTP();
  };

  const handleBack = () => {
    setStep('phone');
    setOtp('');
    setError('');
    setSuccess('');
    setOtpExpiry(null);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <MessageSquare className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Login to QodeAris
            </h1>
            <p className="text-gray-600">
              {step === 'phone' 
                ? 'Enter your phone number to get started' 
                : 'Enter the OTP sent to your WhatsApp'
              }
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-green-800 text-sm">{success}</p>
            </div>
          )}

          {step === 'phone' ? (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="flex gap-2">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="w-24 px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900 bg-white"
                    disabled={loading}
                  >
                    <option value="+91">+91</option>
                    <option value="+1">+1</option>
                    <option value="+44">+44</option>
                    <option value="+971">+971</option>
                  </select>
                  <div className="flex-1 relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                      placeholder="9876543210"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                      disabled={loading}
                      maxLength={10}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Name {isNewUser && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your full name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                  disabled={loading}
                />
              </div>

              <button
                onClick={handleRequestOTP}
                disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending OTP...
                  </>
                ) : (
                  <>
                    <MessageSquare className="w-5 h-5" />
                    Send OTP via WhatsApp
                  </>
                )}
              </button>

              <div className="flex items-center gap-4 !mt-2" aria-hidden="true">
                <span className="flex-1 h-px bg-gray-200" />
                <span className="text-xs font-medium text-gray-400">or</span>
                <span className="flex-1 h-px bg-gray-200" />
              </div>

              <button
                type="button"
                onClick={handleAlreadyHaveOTP}
                disabled={loading || phone.trim().length < 10}
                className="w-full !mt-2 border-2 border-green-600 text-green-600 hover:border-green-700 hover:text-green-700 font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 disabled:bg-gray-200 disabled:border-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
              >
                I already have an OTP
              </button>

              {/* <div className="text-center">
                <p className="text-sm text-gray-600">
                  Secure WhatsApp authentication for your account
                </p>
              </div> */}
            </div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); handleVerifyOTP(); }} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4 text-center">
                  Enter 6-digit OTP
                </label>
                <OTPInput
                  length={6}
                  value={otp}
                  onChange={setOtp}
                  disabled={loading}
                  error={!!error}
                  autoFocus={step === 'otp'}
                />
                {timeLeft > 0 && (
                  <p className="text-center mt-3 text-sm text-gray-600">
                    OTP expires in <span className="font-semibold text-green-600">{formatTime(timeLeft)}</span>
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    {isNewUser ? 'Register' : 'Login'}
                  </>
                )}
              </button>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={loading}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={loading || timeLeft > 0}
                  className="flex-1 bg-blue-100 hover:bg-blue-200 text-blue-700 font-semibold py-3 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {timeLeft > 0 ? `Resend (${formatTime(timeLeft)})` : 'Resend OTP'}
                </button>
              </div>

              <p className="text-center text-xs text-gray-500">
                OTP sent to {countryCode} {phone}
              </p>
            </form>
          )}
        </div>

        <div className="mt-6 text-center text-sm text-gray-600">
          <p>Secure authentication powered by WhatsApp</p>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppAuth;
